// src/v3/Modal/index.tsx
import {
  useOverlayTriggerState
} from "react-stately";

// src/v3/Modal/Modal.tsx
import { useRef } from "react";
import {
  Overlay,
  useModalOverlay
} from "react-aria";
import { cva, cx } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var modalV3Var = cva(
  "ui-overflow-auto ui-z-90 ui-max-h-[calc(100vh-225px)] ui-relative ui-inset-0 ui-transition ui-ease-in-out ui-duration-75 ui-w-full ui-flex-0",
  {
    variants: {
      variant: {
        lightest: "ui-bg-sandstone-100 ui-text-sandstone-500",
        light: "ui-bg-sandstone-200 ui-text-sandstone-500",
        dark: "ui-bg-sandstone-500 ui-text-sandstone-200",
        white: "ui-bg-white ui-text-sandstone-500"
      },
      size: {
        small: "md:ui-w-1/2 md:ui-max-h-1/2",
        medium: "md:ui-w-9/12 md:ui-max-h-9/12",
        large: "md:ui-w-10/12 md:ui-max-h-[83%]",
        xlarge: "md:ui-w-full md:ui-max-h-full",
        full: ""
      },
      padding: {
        small: "ui-p-4",
        medium: "ui-p-6",
        large: "ui-p-8",
        xlarge: "ui-p-10"
      },
      rounded: {
        true: "ui-rounded-xl"
      }
    },
    defaultVariants: {
      variant: "light",
      size: "medium",
      padding: "medium",
      rounded: true
    }
  }
);
function Modal(props) {
  const { blur, className, children, padding, rounded, size, state, variant } = props;
  const ref = useRef(null);
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);
  if (!state.isOpen) {
    return null;
  }
  return /* @__PURE__ */ jsx(Overlay, {
    children: /* @__PURE__ */ jsx("div", {
      className: cx(
        "ui-fixed ui-inset-0 ui-z-200 ui-bg-[#363531]/10 ui-duration-75 ui-ease-in ui-h-full ui-w-full",
        {
          "ui-backdrop-blur-[10px]": blur
        }
      ),
      ...underlayProps,
      "data-cy": "modalBackground",
      children: /* @__PURE__ */ jsx("div", {
        className: cx({
          "ui-fixed ui-inset-0 ui-z-[200] ui-h-full ui-w-full ui-flex ui-items-center ui-justify-center": state.isOpen
        }),
        children: /* @__PURE__ */ jsx("div", {
          ...modalProps,
          className: modalV3Var({
            variant,
            size,
            padding,
            rounded,
            className
          }),
          ref,
          children
        })
      })
    })
  });
}

// src/v3/Modal/Dialog.tsx
import { useRef as useRef2, Children, useState, useEffect } from "react";
import { useDialog } from "react-aria";
import { cva as cva2, cx as cx4 } from "class-variance-authority";

// src/icons/CardExpandCloseIcon.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function CardExpandCloseIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx2("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx2("rect", {
        fill: "currentColor",
        height: 13,
        transform: "rotate(45 12.5356 4.05023)",
        width: 2,
        x: 8.5,
        y: 3.5
      }),
      /* @__PURE__ */ jsx2("rect", {
        fill: "currentColor",
        height: 13,
        transform: "rotate(135 13.9497 12.5355)",
        width: 2,
        x: 13.9497,
        y: 16
      })
    ]
  });
}

// src/react-intl/FormattedMessage.tsx
import { FormattedMessage as ReactIntlFormattedMessage } from "react-intl";

// src/v3/BodyLink/index.tsx
import { forwardRef, useContext } from "react";
import { cx as cx2 } from "class-variance-authority";

// src/contexts/ComponentProvider.tsx
import { createContext, useMemo } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var GLOBAL_KEY = "__@jouzen/ecom-components:ComponentContext__";
function getOrCreateContext() {
  const g = globalThis;
  if (!g[GLOBAL_KEY])
    g[GLOBAL_KEY] = createContext({});
  return g[GLOBAL_KEY];
}
var Context = getOrCreateContext();
function ComponentProvider({
  children,
  LinkComponent,
  ImageComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx3(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/v3/BodyLink/index.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var BodyLink = forwardRef(
  function BodyLink2({
    children,
    className,
    color = "sandstone-500",
    external = false,
    href = "",
    onClick,
    ...rest
  }, ref) {
    const context = useContext(Context);
    const LinkComponent = external || !context.LinkComponent ? "a" : context.LinkComponent;
    return /* @__PURE__ */ jsx4(LinkComponent, {
      "data-cy": "body_link",
      ...rest,
      className: cx2(`ui-underline ui-text-${color}`, className),
      href,
      onClick: (evt) => onClick == null ? void 0 : onClick(evt),
      ref,
      children
    });
  }
);
BodyLink.displayName = "BodyLink";

// src/react-intl/htmlFormatting.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var htmlFormatting = {
  br: /* @__PURE__ */ jsx5("br", {}),
  w: /* @__PURE__ */ jsx5("wbr", {}),
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
  break: function breakTag() {
    return /* @__PURE__ */ jsx5("br", {});
  },
  strong: function strong(chunks) {
    return /* @__PURE__ */ jsx5("strong", {
      children: chunks
    });
  },
  b: function b(chunks) {
    return /* @__PURE__ */ jsx5("strong", {
      children: chunks
    });
  },
  em: function em(chunks) {
    return /* @__PURE__ */ jsx5("em", {
      children: chunks
    });
  },
  i: function i(chunks) {
    return /* @__PURE__ */ jsx5("em", {
      children: chunks
    });
  },
  u: function u(chunks) {
    return /* @__PURE__ */ jsx5("span", {
      className: "ui-underline",
      children: chunks
    });
  },
  sup: function sup(chunks) {
    return /* @__PURE__ */ jsx5("sup", {
      className: "ui-font-serif ui-italic ui-text-blue-200 ui-font-semibold ui-tracking-wider",
      children: chunks
    });
  },
  li: function li(chunks) {
    return /* @__PURE__ */ jsx5("li", {
      children: chunks
    });
  },
  ExternalLink: function ExternalLink(href) {
    return /* @__PURE__ */ jsx5(BodyLink, {
      href,
      rel: "noreferrer",
      target: "_blank",
      children: href
    });
  },
  EmailLink: function EmailLink(email) {
    return /* @__PURE__ */ jsx5(BodyLink, {
      href: `mailto:${email}`,
      children: email
    });
  }
};

// src/react-intl/stringFormatting.ts
var stringFormatting = Object.entries(
  htmlFormatting
).reduce((obj, [key, value]) => {
  const type = typeof value;
  if (type === "string") {
    return { ...obj, [key]: value };
  } else if (type === "function") {
    return { ...obj, [key]: (chunks) => chunks };
  }
  return { ...obj, [key]: "" };
}, {});

// src/react-intl/getLocaleFormatting.tsx
var getLocaleFormatting = (options = {}) => {
  const { stripHtml } = options;
  if (stripHtml) {
    return stringFormatting;
  }
  return htmlFormatting;
};
getLocaleFormatting.displayName = "getLocaleFormatting";
var getLocaleFormatting_default = getLocaleFormatting;

// src/react-intl/FormattedMessage.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function FormattedMessage({
  id,
  values: optionalValues,
  ...props
}) {
  const localeFormatting = getLocaleFormatting_default();
  const values = {
    ...localeFormatting,
    ...optionalValues
  };
  return /* @__PURE__ */ jsx6(ReactIntlFormattedMessage, {
    id,
    values,
    ...props
  });
}
function t(id, values) {
  return /* @__PURE__ */ jsx6(FormattedMessage, {
    id,
    values
  });
}
FormattedMessage.displayName = "FormattedMessage";

// src/v3/Modal/ModalTrigger.tsx
import { useFocusRing, mergeProps } from "react-aria";
import {
  cloneElement
} from "react";
import { cx as cx3 } from "class-variance-authority";
import { jsx as jsx7 } from "react/jsx-runtime";
function ModalTrigger({
  className,
  onClick,
  triggerElement,
  ...props
}) {
  const { focusProps, isFocusVisible } = useFocusRing();
  const clonedElement = cloneElement(triggerElement, {
    ...mergeProps(focusProps, props),
    className: cx3("ui-size-10 ui-flex ui-items-center ui-justify-center", {
      "focus:ui-outline-none": !isFocusVisible
    }),
    onClick,
    ...props
  });
  return /* @__PURE__ */ jsx7("div", {
    className: cx3(className, "ui-translate-x-[13px] -ui-translate-y-[10px]"),
    children: clonedElement
  });
}

// src/v3/Modal/Dialog.tsx
import { Fragment, jsx as jsx8, jsxs as jsxs2 } from "react/jsx-runtime";
var stickyFooterPadding = cva2("ui-px-4 ui-h-fit", {
  variants: {
    variant: {
      lightest: "ui-bg-sandstone-100",
      light: "ui-bg-sandstone-200",
      dark: "ui-bg-sandstone-500",
      white: "ui-bg-white"
    },
    padding: {
      small: "ui-pb-4",
      medium: "ui-pb-6",
      large: "ui-pb-8",
      xlarge: "ui-pb-10"
    }
  },
  defaultVariants: {
    variant: "light",
    padding: "medium"
  }
});
var stickyFooterMargin = cva2(
  "ui-absolute ui-h-fit ui-w-full ui-bottom-[0%] ui-left-[0%]",
  {
    variants: {
      padding: {
        small: "-ui-mb-4",
        medium: "-ui-mb-6",
        large: "-ui-mb-8",
        xlarge: "-ui-mb-10"
      }
    },
    defaultVariants: {
      padding: "medium"
    }
  }
);
function Dialog(props) {
  const { children, variant, onX, fixedFooter, padding } = props;
  const ref = useRef2(null);
  const fixedFooterRef = useRef2(null);
  const [offsetHeight, setOffsetHeight] = useState(16);
  const { dialogProps, titleProps } = useDialog(
    {
      ...props,
      role: "dialog"
    },
    ref
  );
  useEffect(() => {
    setOffsetHeight((currentOffsetHeight) => {
      if (fixedFooterRef.current !== null) {
        return fixedFooterRef.current.clientHeight + currentOffsetHeight;
      }
      return currentOffsetHeight;
    });
  }, [fixedFooterRef]);
  const renderChildren = () => {
    return Children.map(children, (child) => {
      return /* @__PURE__ */ jsx8("div", {
        ...titleProps,
        children: child
      });
    });
  };
  return /* @__PURE__ */ jsxs2(Fragment, {
    children: [
      /* @__PURE__ */ jsx8("div", {
        className: "ui-sticky ui-top-[100%] ui-left-[0%] ui-z-[120]",
        children: /* @__PURE__ */ jsx8("div", {
          className: stickyFooterMargin({
            padding
          }),
          children: /* @__PURE__ */ jsx8("div", {
            className: stickyFooterPadding({
              variant,
              padding
            }),
            children: /* @__PURE__ */ jsx8("div", {
              className: "tailwind",
              ref: fixedFooterRef,
              children: fixedFooter
            })
          })
        })
      }),
      /* @__PURE__ */ jsxs2("div", {
        ...dialogProps,
        className: cx4("focus:ui-outline-none"),
        ref,
        children: [
          /* @__PURE__ */ jsx8("div", {
            className: "ui-flex ui-justify-end",
            children: /* @__PURE__ */ jsx8(ModalTrigger, {
              className: "ui-fixed ui-w-fit",
              "data-cy": "xModalTrigger",
              onClick: onX,
              triggerElement: /* @__PURE__ */ jsxs2("button", {
                "data-cy": "closeButton",
                type: "button",
                children: [
                  /* @__PURE__ */ jsx8("span", {
                    className: "ui-sr-only",
                    children: t("close")
                  }),
                  /* @__PURE__ */ jsx8(CardExpandCloseIcon, {
                    className: cx4(
                      { "ui-text-sandstone-200": variant === "dark" },
                      {
                        "ui-text-sandstone-500": variant === "light" || variant === "white"
                      }
                    )
                  })
                ]
              })
            })
          }),
          /* @__PURE__ */ jsx8("div", {
            className: cx4("tailwind ui-pt-4 ui-px-4"),
            style: { paddingBottom: `${offsetHeight}px` },
            children: /* @__PURE__ */ jsx8("div", {
              className: "tailwind",
              children: renderChildren()
            })
          })
        ]
      })
    ]
  });
}

// src/v3/Modal/index.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function Modal2({
  blur = true,
  className,
  children,
  variant = "light",
  size = "medium",
  padding = "medium",
  rounded,
  fixedFooter,
  onClickXFunc,
  ...props
}) {
  const state = useOverlayTriggerState(props);
  const handleXClose = () => {
    state.close();
    if (onClickXFunc) {
      onClickXFunc();
    }
  };
  return /* @__PURE__ */ jsx9("div", {
    children: /* @__PURE__ */ jsx9(Modal, {
      blur,
      className,
      isDismissable: true,
      padding,
      rounded,
      size,
      state,
      variant,
      children: /* @__PURE__ */ jsx9(Dialog, {
        fixedFooter,
        onX: handleXClose,
        padding,
        variant,
        children
      })
    })
  });
}
Modal2.displayName = "Modal";
export {
  Modal2 as Modal,
  ModalTrigger
};
//# sourceMappingURL=index.mjs.map